// File: mbrs-v2-react/src/components/UsageLogs.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from './MainLayout';
import { useSession } from '../contexts/SessionContext';
import { useCompany } from '../contexts/CompanyContext';
import { supabase } from '../lib/supabase_client';
import { generateSSTInvoice, generateOldInvoice, generateRegularInvoice, InvoiceTransactionData, InvoiceCustomerData } from "../services/invoiceService";
import UserSales from './BillingHistory';
import * as XLSX from 'xlsx';
import '../styles/page-base.css';

type TabType = 'transaction' | 'billing' | 'referrals';

interface WalletTransaction {
  id: string;
  created_at: string;
  kind: 'topup' | 'bonus' | 'spend' | 'refund' | 'adjustment';
  amount_numeric: number;
  reference_type: string | null;
  reference_id: string | null;
  meta: any;
  created_by: string | null;
  action_by_name: string | null;
  description: string;
  // Calculated fields
  credit_movement: number;
//   free_credit_movement: number;
  closing_credit_balance: number;
  closing_free_credit_balance: number;
}

interface Referral {
    id: string;
    company_name: string;
    status: string;
    created_at: string;
}

export default function UsageLogs() {
  const { hasPermission, loading: sessionLoading } = useSession();
  const { selectedCompany } = useCompany();
  const { companyId: urlCompanyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [filingCompanyNames, setFilingCompanyNames] = useState<Record<string, string>>({});
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [transactionType, setTransactionType] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  // Check permission
  const canViewUsageLogs = hasPermission('usage_logs_view');
  const canViewBilling = hasPermission('bilings');

  const allTabs = [
    { 
      id: 'transaction' as TabType, 
      label: 'Transaction History',
      permission: canViewUsageLogs,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      )
    },
    { 
      id: 'billing' as TabType, 
      label: 'Billing History', 
      permission: canViewBilling,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      )
    },
    { 
      id: 'referrals' as TabType, 
      label: 'Referrals',
      permission: canViewUsageLogs,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
        </svg>
      )
    },
  ];

  const tabs = allTabs.filter(tab => tab.permission);
  const defaultTab = tabs.length > 0 ? tabs[0].id : 'transaction';
  const [activeTab, setActiveTab] = useState<TabType>(defaultTab);

  useEffect(() => {
    const availableTabIds = tabs.map(t => t.id);
    if (!availableTabIds.includes(activeTab) && tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
  }, [canViewBilling, activeTab]);

  const fetchTransactions = useCallback(async () => {
    if (!selectedCompany?.company_id) return;
  
    try {
      setLoading(true);

      // Call edge function to get transactions
      const { data, error } = await supabase.functions.invoke('usage-logs', {
        body: {
          org_id: selectedCompany.company_id
        }
      });
  
      if (error) {
        console.error('Error fetching transactions:', error);
        setTransactions([]);
        return;
      }
  
      if (data?.error) {
        console.error('Error from edge function:', data.error);
        setTransactions([]);
        return;
      }
  
      const fetchedTransactions: WalletTransaction[] = data?.transactions || [];
      setTransactions(fetchedTransactions);

      // ✅ Fetch company names for filing transactions
      const filingIds = fetchedTransactions
        .filter((tx: WalletTransaction) => tx.kind === 'spend' && 
                      tx.reference_type === 'filings' && 
                      tx.reference_id)
        .map((tx: WalletTransaction) => tx.reference_id!)
        .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

      if (filingIds.length > 0) {
        const { data: filings, error: filingError } = await supabase
          .from('filings')
          .select('id, company_info')
          .in('id', filingIds);

        if (!filingError && filings) {
          const companyNameMap: Record<string, string> = {};
          filings.forEach((filing: any) => {
            // ✅ Add ALL fetched filings to map (even if no company name)
            // This lets us distinguish: filing exists vs filing deleted
            if (filing.company_info && typeof filing.company_info === 'object') {
              const companyName = filing.company_info.company_name;
              // Use empty string as marker for "filing exists but no company name"
              companyNameMap[filing.id] = companyName || '';
            } else {
              // No company_info at all - mark as empty
              companyNameMap[filing.id] = '';
            }
          });
          setFilingCompanyNames(companyNameMap);
        }
      } else {
        setFilingCompanyNames({});
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany?.company_id]);

  const fetchReferrals = useCallback(async () => {
    if (!selectedCompany?.company_id) return;
  
    try {
      setLoading(true);
      
      const { data: referredOrgs, error } = await supabase
        .from('organizations')
        .select('id, name, created_at')
        .eq('referred_by', selectedCompany.company_id)
        .order('created_at', { ascending: false });
  
      if (error) {
        console.error('Error fetching referrals:', error);
        return;
      }
  
      const transformedData: Referral[] = (referredOrgs || []).map((org: any) => ({
        id: org.id,
        company_name: org.name || '-',
        status: 'active',
        created_at: org.created_at
      }));
  
      setReferrals(transformedData);
    } catch (error) {
      console.error('Error fetching referrals:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany?.company_id]);

  useEffect(() => {
    if (canViewUsageLogs && selectedCompany) {
      if (activeTab === 'transaction') {
        fetchTransactions();
      } else if (activeTab === 'referrals') {
        fetchReferrals();
      }
      // billing tab doesn't need data fetching (UserSales handles it)
    }
  }, [activeTab, selectedCompany?.company_id, canViewUsageLogs, fetchTransactions, fetchReferrals]);

  useEffect(() => {
    if (selectedCompany?.company_id && selectedCompany.company_id !== urlCompanyId) {
      // Update URL when selectedCompany changes
      navigate(`/usage-logs/${selectedCompany.company_id}`, { replace: true });
    }
  }, [selectedCompany?.company_id, urlCompanyId, navigate]);
  
  const getNatureDisplay = (kind: string) => {
    switch (kind) {
      case 'topup':
        return 'topup';
      case 'bonus':
        return 'referral'; // Hardcoded as requested
      case 'spend':
        return 'usage';
      case 'adjustment':
        return 'adjust';
      case 'refund':
        return 'refund';
      default:
        return kind;
    }
  };

  const formatMovement = (movement: number) => {
    if (movement === 0) return '0';
    if (movement > 0) return `+${movement}`;
    return `${movement}`;
  };

  // Helper function to get the displayed description (used in both search and render)
  const getDisplayedDescription = (transaction: WalletTransaction): string => {
    // Format description for upload filings
    if (transaction.kind === 'spend' && 
        transaction.reference_type === 'filings' && 
        transaction.description === 'Upload Filings' &&
        transaction.reference_id) {
      const filingId = transaction.reference_id;
      
      // Three cases:
      // 1. Filing exists WITH company name → show company name
      // 2. Filing exists WITHOUT company name → show "Unnamed Company"
      // 3. Filing deleted/not found → show "Filing Deleted"
      
      if (filingCompanyNames.hasOwnProperty(filingId)) {
        // Filing was fetched - check if it has a company name
        const companyName = filingCompanyNames[filingId];
        if (companyName && companyName.trim() !== '') {
          return companyName; // Case 1: Has company name
        } else {
          return 'Unnamed Company'; // Case 2: Exists but no name
        }
      } else {
        // Filing ID not in map - filing was deleted or soft deleted
        return 'Filing Deleted'; // Case 3: Filing not found
      }
    }
    // For topup and other transactions, show the description if available
    if (transaction.description && transaction.description !== '[description]') {
      return transaction.description;
    }
    // Fallback to reference_type
    return transaction.reference_type || '';
  };

  // Helper function to download invoice for topup transactions
  const handleDownloadInvoice = async (purchaseId: string) => {
    try {
      // Fetch purchase data
      const { data: purchase, error: purchaseError } = await supabase
        .from('org_purchases')
        .select(`
          id,
          amount,
          status,
          created_at,
          updated_at,
          org_id,
          product_id,
          payment_reference,
          sequential_invoice_number,
          invoice_version
        `)
        .eq('id', purchaseId)
        .eq('status', 'paid')
        .maybeSingle();
  
      if (purchaseError || !purchase) {
        alert('Invoice not found or not available.');
        return;
      }
  
      // Fetch product data
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('id, name, credit_amount, country_iso, currency')
        .eq('id', purchase.product_id)
        .maybeSingle();
  
      if (productError) {
        console.error('Error fetching product:', productError);
      }
  
      // Use selectedCompany instead of fetching org (same as BillingHistory)
      if (!selectedCompany) {
        alert('Company information not available.');
        return;
      }
  
      // Prepare customer data (same approach as BillingHistory)
      let customerData: InvoiceCustomerData = {
        company_name: selectedCompany.company_name || 'Unknown Company',
        billing_address: '',
        contact_phone: undefined,
        contact_email: undefined
      };
  
      // Fetch organization address only
      const { data: orgData } = await supabase
        .from('organizations')
        .select('address')
        .eq('id', purchase.org_id)
        .maybeSingle();
  
      if (orgData?.address) {
        customerData.billing_address = orgData.address;
      }
  
      // Fetch organization owner for contact info (same as BillingHistory)
      const { data: ownerMember } = await supabase
        .from('organization_members')
        .select('user_id')
        .eq('org_id', purchase.org_id)
        .eq('role_id', 1)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
  
      if (ownerMember?.user_id) {
        const { data: userDetails } = await supabase
          .rpc('get_user_details', { user_ids: [ownerMember.user_id] });
  
        if (userDetails && userDetails.length > 0) {
          customerData.contact_email = userDetails[0].email || undefined;
          customerData.contact_phone = userDetails[0].contact_number || undefined;
        }
      }
  
      // Prepare transaction data
      const transactionData: InvoiceTransactionData = {
        id: purchase.id,
        created_at: purchase.created_at,
        amount: purchase.amount,
        credits: product?.credit_amount || 0,
        products: {
          name: product?.name || 'Unknown Product'
        },
        sequential_invoice_number: purchase.sequential_invoice_number
      };
  
      // Generate invoice based on version
      const invoiceVersion = purchase.invoice_version || 1;
      let pdfBlob: Blob;
  
      if (invoiceVersion === 3) {
        pdfBlob = await generateSSTInvoice(transactionData, customerData);
      } else if (invoiceVersion === 2) {
        pdfBlob = await generateRegularInvoice(transactionData, customerData);
      } else {
        pdfBlob = await generateOldInvoice(transactionData, customerData);
      }
  
      // Create download link
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = pdfUrl;
  
      let filename: string;
      if (invoiceVersion === 3) {
        const sequentialNumber = purchase.sequential_invoice_number;
        filename = `INV${sequentialNumber?.toString().padStart(6, '0')}.pdf`;
      } else {
        let invoiceId = purchase.id;
        if (invoiceId.includes('-')) {
          invoiceId = invoiceId.split('-')[0];
        } else {
          invoiceId = invoiceId.substring(0, 8);
        }
        filename = `invoice_${invoiceId}.pdf`;
      }
  
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
  
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Error generating invoice. Please try again.');
    }
  };

  // Export filings list to Excel
  const handleExportFilings = async () => {
    if (!selectedCompany?.company_id) return;

    try {
      const { data, error } = await supabase.rpc('get_filings_for_org', {
        org_id_param: selectedCompany.company_id
      });

      if (error || !data || data.length === 0) {
        console.error('Error or no data:', error);
        return;
      }

      const exportData = data.map((filing: any) => ({
        'Company Name': filing.company_name || '',
        'Year End': filing.year_end || '',
        'Status': filing.status || '',
        'Consolidated': filing.consolidated === 'Y' ? 'Yes' : filing.consolidated === 'N' ? 'No' : (filing.consolidated || ''),
        'Created At': filing.created_at || '',
        'Created By': filing.created_by_name || '',
        'Updated At': filing.updated_at || '',
        'Updated By': filing.updated_by_name || '',
        'Pinned': filing.pinned_at ? 'Yes' : 'No'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 40 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
        { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 8 }
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Filings');

      const orgName = selectedCompany.company_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'filings';
      const dateStr = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `${orgName}_filings_${dateStr}.xlsx`);
    } catch (error) {
      console.error('Error exporting filings:', error);
    }
  };
  
  const renderTransactionHistory = () => {
    // Apply filters
    let filtered = transactions.filter(t => {
      // Search filter - use computed description
      const displayedDesc = getDisplayedDescription(t);
      const matchesSearch = !searchTerm || 
        displayedDesc.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.reference_type && t.reference_type.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Transaction type filter (nature/kind)
      const matchesType = !transactionType || 
        t.kind.toLowerCase() === transactionType.toLowerCase() ||
        (transactionType === 'referral' && t.kind === 'bonus');
      
      // Date range filter
      let matchesDateRange = true;
      if (fromDate || toDate) {
        const transactionDate = new Date(t.created_at);
        if (fromDate) {
          const from = new Date(fromDate);
          from.setHours(0, 0, 0, 0);
          if (transactionDate < from) {
            matchesDateRange = false;
          }
        }
        if (toDate) {
          const to = new Date(toDate);
          to.setHours(23, 59, 59, 999);
          if (transactionDate > to) {
            matchesDateRange = false;
          }
        }
      }
      
      return matchesSearch && matchesType && matchesDateRange;
    });

    const totalPages = Math.ceil(filtered.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentTransactions = filtered.slice(startIndex, endIndex);

    const handleClear = () => {
      setTransactionType('');
      setFromDate('');
      setToDate('');
      setCurrentPage(1);
    };

    return (
        <>
          {/* Filter Section - Separate page-card */}
          <div className="page-card mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              {/* Transaction Type Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  Transaction Type
                </label>
                <select
                  value={transactionType}
                  onChange={(e) => setTransactionType(e.target.value)}
                  className="block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="topup">Topup</option>
                  <option value="referral">Referral</option>
                  <option value="spend">Spent</option>
                  <option value="adjustment">Adjust</option>
                  <option value="refund">Refund</option>
                </select>
              </div>
  
              {/* From Date Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
  
              {/* To Date Filter */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                  To Date
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
  
              {/* Filter and Clear Buttons */}
              <div className="flex gap-2">
                {/* <button
                  onClick={handleFilter}
                  className="f-btn f-btn-primary"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  Filter
                </button> */}
                <button
                  onClick={handleClear}
                  className="f-btn f-btn-gray"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Clear
                </button>
              </div>
            </div>
          </div>
  
          {/* Search Bar and Table - Separate page-card */}
          <div className="page-card">  
          {/* Search Bar */}
          <div className="flex items-center justify-between mb-6">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full text-sm pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
            <table className="page-table">
              <thead className="page-table-thead">
                <tr className="page-table-tr">
                <th className="page-table-th">Date</th>
                <th className="page-table-th">Nature</th>
                <th className="page-table-th">Description</th>
                <th className="page-table-th">
                <div className="text-center">Movement</div>
                <div className="text-center text-xs font-normal mt-1 pt-1">Credit</div>
                </th>
                <th className="page-table-th">Action By</th>
                <th className="page-table-th">
                <div className="text-center">Closing Balance</div>
                <div className="text-center text-xs font-normal mt-1 pt-1">Credit</div>
                </th>
                <th className="page-table-th" style={{ textAlign: 'center' }}>Export</th>
                </tr>
              </thead>
              <tbody className="page-table-tbody">
              {loading ? (
                <tr>
                    <td colSpan={7} className="text-center py-4 text-gray-500">
                    Loading...
                    </td>
                </tr>
                ) : currentTransactions.length === 0 ? (
                <tr>
                    <td colSpan={7} className="text-center py-4 text-gray-500">
                    No transactions found.
                    </td>
                </tr>
                ) : (
                  currentTransactions.map((transaction) => (
                    <tr key={transaction.id} className="page-table-tr">
                      <td className="page-table-td">
                      {(() => {
                        const dateObj = new Date(transaction.created_at);
                        const dateStr = dateObj.toLocaleDateString('en-GB');
                        const timeStr = dateObj.toLocaleTimeString('en-GB', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: true 
                        });
                        return (
                          <div className="flex flex-col">
                            <span>{dateStr}</span>
                            <span className="text-gray-500 text-xs">{timeStr}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="page-table-td">
                        <span className={getNatureBadgeStyle(transaction.kind)}>
                          {getNatureDisplay(transaction.kind)}
                        </span>
                    </td>
                    <td className="page-table-td">
                      {getDisplayedDescription(transaction)}
                    </td>
                    <td className="page-table-td text-center">
                    <span className={getMovementColor(transaction.credit_movement)}>
                        {transaction.credit_movement === 0 ? '0' : formatMovement(transaction.credit_movement)}
                    </span>
                    </td>
                    <td className="page-table-td">
                      {transaction.action_by_name === 'API Upload' ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700">
                          API Upload
                        </span>
                      ) : (
                        transaction.action_by_name || 'System'
                      )}
                    </td>
                    <td className="page-table-td text-center">
                    {formatBalance(transaction.closing_credit_balance)}
                    </td>
                    <td className="page-table-td text-center">
                    {(() => {
                        // Topup transactions with purchase reference
                        if (transaction.kind === 'topup' && transaction.reference_type === 'purchase' && transaction.reference_id) {
                        return (
                            <button
                            onClick={() => handleDownloadInvoice(transaction.reference_id!)}
                            className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-200 transition flex items-center space-x-1 mx-auto"
                            title="Download Invoice"
                            >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>Invoice</span>
                            </button>
                        );
                        }
                        
                        // Referral and other transactions - no link
                        return <span className="text-gray-400">-</span>;
                    })()}
                    </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

            {/* Pagination - Matching BillingHistory design */}
            {!loading && filtered.length > 0 && (
                <div className="flex items-center justify-between text-sm text-gray-600 mt-6">
                <div className="flex items-center space-x-2">
                    <span>Rows per page:</span>
                    <select 
                    value={rowsPerPage} 
                    onChange={(e) => setRowsPerPage(Number(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1"
                    >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    </select>
                </div>
                <span>
                    {startIndex + 1}-{Math.min(endIndex, filtered.length)} of {filtered.length}
                </span>
                <div className="flex space-x-1">
                    <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                    >
                    &lt;
                    </button>
                    <button className="f-btn f-btn-primary f-btn-xs">
                    {currentPage}
                    </button>
                    <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                    >
                    &gt;
                    </button>
                </div>
                </div>
            )}
          </div>
        </>
      );
  };

  const renderReferrals = () => {
    const filtered = referrals.filter(r =>
      r.company_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filtered.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const currentReferrals = filtered.slice(startIndex, endIndex);

    return (
      <div className="page-card">
        <div className="flex items-center justify-between mb-6">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by company name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full text-sm pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
          <table className="page-table">
            <thead className="page-table-thead">
              <tr className="page-table-tr">
                <th className="page-table-th">Date</th>
                <th className="page-table-th">Company Name</th>
                <th className="page-table-th">Status</th>
              </tr>
            </thead>
            <tbody className="page-table-tbody">
              {loading ? (
                <tr>
                  <td colSpan={3} className="text-center py-4 text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : currentReferrals.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center py-4 text-gray-500">
                    No referrals found.
                  </td>
                </tr>
              ) : (
                currentReferrals.map((referral) => (
                  <tr key={referral.id} className="page-table-tr">
                    <td className="page-table-td">
                      {(() => {
                        const dateObj = new Date(referral.created_at);
                        const dateStr = dateObj.toLocaleDateString('en-GB');
                        const timeStr = dateObj.toLocaleTimeString('en-GB', { 
                          hour: '2-digit', 
                          minute: '2-digit',
                          hour12: true 
                        });
                        return (
                          <div className="flex flex-col">
                            <span>{dateStr}</span>
                            <span className="text-gray-500 text-xs">{timeStr}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="page-table-td">{referral.company_name}</td>
                    <td className="page-table-td">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getReferralStatusColor(referral.status)}`}>
                        {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between text-sm text-gray-600 mt-6">
            <div className="flex items-center space-x-2">
              <span>Rows per page:</span>
              <select 
                value={rowsPerPage} 
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <span>
              {startIndex + 1}-{Math.min(endIndex, filtered.length)} of {filtered.length}
            </span>
            <div className="flex space-x-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
              >
                &lt;
              </button>
              <button className="f-btn f-btn-primary f-btn-xs">
                {currentPage}
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
              >
                &gt;
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const getReferralStatusColor = (status: string) => {
    return status.toLowerCase() === 'active' 
      ? 'bg-green-100 text-green-700' 
      : 'bg-gray-100 text-gray-700';
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'transaction':
        return renderTransactionHistory();
      case 'billing':
        return <UserSales />;
      case 'referrals':
        return renderReferrals();
      default:
        // If no tabs available, show message
        if (tabs.length === 0) {
          return (
            <div className="page-card">
              <div className="text-center py-12">
                <div className="text-gray-500 text-lg">No Access</div>
                <div className="text-gray-400 text-sm mt-2">
                  You don't have permission to view any usage logs tabs.
                </div>
              </div>
            </div>
          );
        }
        return tabs.length > 0 ? renderTransactionHistory() : null;
    }
  };

  const formatBalance = (balance: number) => {
    // Remove .00 if it's a whole number
    if (balance % 1 === 0) {
      return balance.toString();
    }
    return balance.toFixed(2);
  };

  const getMovementColor = (movement: number) => {
    if (movement > 0) return 'text-green-600';
    if (movement < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  const getNatureBadgeStyle = (kind: string) => {
    switch (kind) {
      case 'spend':
        // Light orange background with darker text
        return 'bg-orange-200 text-orange-800 px-2 py-1 rounded text-xs font-semibold uppercase';
      case 'topup':
        // Light green background with darker text
        return 'bg-emerald-200 text-emerald-800 px-2 py-1 rounded text-xs font-semibold uppercase';
      case 'bonus':
        // Light purple background with darker text
        return 'bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs font-semibold uppercase';
      case 'adjustment':
        // Light gray background with darker text
        return 'bg-slate-200 text-slate-800 px-2 py-1 rounded text-xs font-semibold uppercase';
      case 'refund':
        // Light blue background with darker text
        return 'bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-semibold uppercase';
      default:
        return 'bg-gray-200 text-gray-800 px-2 py-1 rounded text-xs font-semibold uppercase';
    }
  };

  if (sessionLoading) {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="max-w-6xl mx-auto space-y-6">
            <div className="text-center py-12">
              <div className="text-gray-500">Loading...</div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!canViewUsageLogs) {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="max-w-6xl mx-auto space-y-6">
            <div className="page-header">
              <div className="page-header-left">
                <h1 className="page-title">Usage & Billing</h1>
              </div>
            </div>
            <div className="page-card">
              <div className="text-center py-12">
                <div className="text-gray-500 text-lg">Access Denied</div>
                <div className="text-gray-400 text-sm mt-2">
                  You don't have permission to view usage & billing.
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="page-header">
            <div className="page-header-left">
              <h1 className="page-title">Usage & Billing</h1>
            </div>
            <div className="page-header-right">
              <button
                onClick={handleExportFilings}
                disabled={loading}
                className="f-btn f-btn-primary"
                title="Export filings list to Excel"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Filings
              </button>
            </div>
          </div>

          {/* Tab Navigation - Same style as Permissions page */}
          <div className="page-tabs">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSearchTerm('');
                    setCurrentPage(1);
                  }}
                  className={`
                    group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <span className={`mr-2 ${activeTab === tab.id ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'}`}>
                    {tab.icon}
                  </span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}